import { createSlice } from "@reduxjs/toolkit";

import {
  EMAIL_VERIFICATION_STATUS,
  LOCALSTORAGE_ITEMS,
  LOU_TOURS,
  PASSWORD_UPDATE_STATUS,
  SUBSCRIPTION_STATUS,
  USER_ROLES,
  STRIPE_SUBSCRIPTION_CATEGORIES,
} from "../constants";
import { clearStore } from "actions/initialization";
import {
  loginThunk,
  oauth2LoginThunk,
  authenticateThunk,
  logoutThunk,
  signupThunk,
  verifyEmailThunk,
  resendVerifyEmailThunk,
  forgotPasswordThunk,
  changePasswordThunk,
  usersListThunk,
  groupsListThunk,
  searchUsersAndGroupsThunk,
  ownersListThunk,
  getCreditsThunk,
  sendPromoCodeThunk,
  updateTLevelThunk,
  updateUserDomainInfoThunk,
  fetchUserThunk,
  updateLocalStorageThunk,
  updateDemoWizardThunk,
  deleteAccountThunk,
  updateSettingsThunk,
  getApiKeyThunk,
  createApiKeyThunk,
  deleteApiKeyThunk,
  npsSurveyThunk,
} from "thunks/auth";
import {
  isUserSubscribed,
  isPendingAction,
  isFulfilledAction,
  isRejectedAction,
  isObject,
} from "utils";
import { logger } from "utils/logger";
import localStorageService from "services/localStorage";

export const initialState = {
  isAuthenticated: false,
  isSubscribed: false,
  isTrial: false,
  isTrialExpired: false,
  isAdmin: false,
  isPrepaid: false,
  isAbleToCreate: false,
  isOnTtsPlan: false,
  user: {
    id: null,
    email: null,
    firstName: null,
    lastName: null,
    roles: [],
    groups: [],
    data: {
      calibration: null,
      download: false,
      advanced_settings: {
        normalize: false,
        denormalize: false,
        denoise: true,
        hft: true,
        emotional_scale: 1,
      },
      promo: {},
    },
  },
  localStorage: {},
  demoWizard: undefined,
  error: null,
  // TODO use this for initial app loading?
  // loadingGlobal: getCsrfToken(),
  // TODO add single loading object with different nested props like `credits: true, promo_code: true, settings: true`
  loadingGlobal: true,
  loading: false,
  loadingSettings: false,
  emailVerification: null,
  verified: false,
  passwordUpdate: null,
  passwordUpdateId: null,
  trialStarted: false,
  shouldFetchCredits: false,
  credits: {
    creditLimit: false,
    credits: 0,
    initialCredits: 0,
    seconds: 0,
    initialSeconds: 0,
    creditsUsed: 0,
    secondsUsed: 0,
    creditsUsedPercentage: 0,
    secondsUsedPercentage: 0,
    availableSeconds: 0, // total available seconds + credits (converted to seconds)
    initialTts: 0,
    ttsUsed: 0,
    ttsUsedPercentage: 0,
    initialTtsRequests: 0,
    ttsRequestsUsed: 0,
    ttsRequestsUsedPercentage: 0,
    availableCharacters: 0,
  },
  search: {
    query: null,
    users: { ids: [], hash: {} },
    groups: { ids: [], hash: {} },
  },
  users: {
    ids: [],
    hash: {},
  },
  groups: {
    ids: [],
    hash: {},
  },
  prices: [],
  activePriceId: null,
  // FIXME remove from store and other places
  lou: {
    [LOU_TOURS.calibrationComplete]: false,
    [LOU_TOURS.originalCreate]: false,
    [LOU_TOURS.conversionCreate]: false,
    [LOU_TOURS.startTour]: false,
    [LOU_TOURS.newProject]: false,
  },
  longConversionsAllowed: false,
  isAbleToDownload: false,
  isPromoCodeResultReceived: false,
  apiKey: undefined,
  // FYI on localStorage update fusion makes 2 updates - null and {...},
  // but sends 2 ws events, and some features are breaks on this behaviour
  shouldWaitForLocalStorage: false,
};

const userDTO = (user) => ({
  id: user.id,
  name: user.username,
  email: user.email,
  firstName: user.first_name,
  lastName: user.last_name,
  fullname: `${user.first_name} ${user.last_name}`,
  groups: user.groups,
  roles: user.roles,
  data: user.data,
});

const actionsToFilter = {
  pending: [
    loginThunk.pending,
    oauth2LoginThunk.pending,
    logoutThunk.pending,
    signupThunk.pending,
    verifyEmailThunk.pending,
    resendVerifyEmailThunk.pending,
    forgotPasswordThunk.pending,
    changePasswordThunk.pending,
    usersListThunk.pending,
    groupsListThunk.pending,
    searchUsersAndGroupsThunk.pending,
    ownersListThunk.pending,
    getCreditsThunk.pending,
    sendPromoCodeThunk.pending,
    updateTLevelThunk.pending,
    updateUserDomainInfoThunk.pending,
    fetchUserThunk.pending,
    updateLocalStorageThunk.pending,
    updateDemoWizardThunk.pending,
    deleteAccountThunk.pending,
    getApiKeyThunk.pending,
    createApiKeyThunk.pending,
    deleteApiKeyThunk.pending,
    // npsSurveyThunk.pending,
  ],
  fulfilled: [
    // authenticateThunk.fulfilled,
    oauth2LoginThunk.fulfilled,
    signupThunk.fulfilled,
  ],
  rejected: [
    authenticateThunk.rejected,
    logoutThunk.rejected,
    forgotPasswordThunk.rejected,
    usersListThunk.rejected,
    groupsListThunk.rejected,
    searchUsersAndGroupsThunk.rejected,
    ownersListThunk.rejected,
    getCreditsThunk.rejected,
    sendPromoCodeThunk.rejected,
    updateTLevelThunk.rejected,
    updateUserDomainInfoThunk.rejected,
    fetchUserThunk.rejected,
    updateLocalStorageThunk.rejected,
    updateDemoWizardThunk.rejected,
    deleteAccountThunk.rejected,
    getApiKeyThunk.rejected,
    createApiKeyThunk.rejected,
    deleteApiKeyThunk.rejected,
    // npsSurveyThunk.rejected,
  ],
};

export const prepareUser = (user, currentState) => {
  const stripeData = user?.data?.stripe;
  const isAdmin = user.roles.includes(USER_ROLES.ADMIN);
  const isPrepaid = user.roles.includes(USER_ROLES.PREPAID);
  const isTrial =
    stripeData &&
    stripeData?.subscription_status &&
    stripeData.trial &&
    stripeData.subscription_status === SUBSCRIPTION_STATUS.TRIALING
      ? true
      : false;
  const isSubscribed = isUserSubscribed(user);
  const isOnTtsPlan =
    stripeData &&
    stripeData?.subscription_category ===
      STRIPE_SUBSCRIPTION_CATEGORIES.tts_only;
  return {
    user: userDTO(user),
    credits: {
      ...currentState?.credits,
      creditLimit: user.data.credit_limit || false,
    },
    isAuthenticated: true,
    isSubscribed,
    isOnTtsPlan,
    isAdmin,
    isPrepaid,
    isTrial,
    isTrialExpired: !isTrial && stripeData?.trial_used && !isSubscribed,
    isAbleToCreate: Boolean(user.data?.permissions?.can_write),
    longConversionsAllowed: Boolean(user.data?.permissions?.long_conversions),
    isAbleToDownload: Boolean(user.data?.permissions?.download),
    verified: user.verified,
    localStorage: user.data?.local_storage || {},
    shouldFetchCredits:
      !isAdmin && (!isPrepaid || (isPrepaid && currentState.creditLimit))
        ? true
        : false,
    demoWizard: {
      ...(user.data?.demo_wizard || {}),
      ignoreWizard:
        !isTrial ||
        !user.data?.demo_wizard?.show ||
        user.data?.demo_wizard?.completed ||
        user.data?.demo_wizard?.skipped,
    },
  };
};

const proceedUser = (state, user) => {
  state.loadingGlobal = false;
  state.loading = false;
  // fusion bug with temporary null
  state.shouldWaitForLocalStorage =
    isObject(state.localStorage) &&
    Object.keys(state.localStorage).length > 0 &&
    ((isObject(user.data?.local_storage) &&
      Object.keys(user.data?.local_storage).length === 0) ||
      !user.data?.local_storage);
  Object.assign(
    state,
    prepareUser(
      {
        ...user,
        data: {
          ...user.data,
          local_storage: user.data?.local_storage
            ? user.data?.local_storage
            : state.localStorage,
        },
      },
      { ...state }
    )
  );
};

export const prepareCredits = (data) => {
  const {
    subscription_seconds: seconds,
    initial_subscription_seconds: initial_seconds,
    bought_credits: creditsUsed,
    initial_bought_credits: initial_credits,
    available_seconds,
    tts_current,
    tts_limit,
    initial_tts_chars,
    tts_chars,
  } = data;
  const credits = initial_credits - creditsUsed;
  const secondsUsed = initial_seconds - seconds;
  const ttsUsed = initial_tts_chars - tts_chars;

  const creditsUsedPercentage = (creditsUsed / initial_credits) * 100 || 0;
  const secondsUsedPercentage = (secondsUsed / initial_seconds) * 100 || 0;
  const ttsRequestsUsedPercentage = (tts_current / tts_limit) * 100 || 0;
  const ttsUsedPercentage = (ttsUsed / initial_tts_chars) * 100 || 0;

  const availableCharacters = initial_tts_chars - ttsUsed;

  return {
    credits,
    initialCredits: initial_credits,
    seconds,
    initialSeconds: initial_seconds,
    creditsUsed,
    secondsUsed,
    creditsUsedPercentage,
    secondsUsedPercentage,
    availableSeconds: available_seconds,
    availableCharacters,
    initialTtsRequests: tts_limit,
    ttsRequestsUsed: tts_current,
    ttsRequestsUsedPercentage,
    initialTts: initial_tts_chars,
    ttsUsed,
    ttsUsedPercentage,
  };
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    userUpdate(state, action) {
      const user = action.payload;
      proceedUser(state, user);
    },
    toggleGlobalLoading(state, action) {
      const { value } = action.payload;
      state.loadingGlobal = value;
    },
    updateCredits(state, action) {
      state.credits = {
        ...state.credits,
        ...prepareCredits(action.payload),
      };
    },
  },
  extraReducers: (builder) => {
    builder
      // #region pending
      .addCase(authenticateThunk.pending, (state) => {
        state.loadingGlobal = true;
      })
      .addCase(updateSettingsThunk.pending, (state) => {
        state.loadingSettings = true;
      })
      // #endregion
      // #region fulfilled
      .addCase(authenticateThunk.fulfilled, (state, action) => {
        const user = action.payload;
        proceedUser(state, user);
        const lastUsedVoices =
          (Boolean(user?.data?.local_storage) &&
            user?.data?.local_storage[LOCALSTORAGE_ITEMS.lastUsedVoices]) ||
          null;
        if (lastUsedVoices) {
          localStorageService.setItem(
            LOCALSTORAGE_ITEMS.lastUsedVoices,
            lastUsedVoices
          );
        }
      })
      .addCase(verifyEmailThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.emailVerification = EMAIL_VERIFICATION_STATUS.SUCCESS;
        state.verified = true;
        const user = action.payload;
        proceedUser(state, user);
      })
      .addCase(resendVerifyEmailThunk.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(forgotPasswordThunk.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(changePasswordThunk.fulfilled, (state) => {
        state.loading = false;
        state.passwordUpdate = PASSWORD_UPDATE_STATUS.SUCCESS;
      })
      .addCase(usersListThunk.fulfilled, (state, action) => {
        const { users = [] } = action.payload;
        state.users.ids = [];
        state.users.hash = {};

        users.forEach((user) => {
          state.users.ids.push(user.id);
          state.users.hash[user.id] = {
            ...user,
            fullname: `${user.first_name} ${user.last_name}`,
          };
        });
      })
      .addCase(groupsListThunk.fulfilled, (state, action) => {
        const { groups = [] } = action.payload;
        state.groups.ids = [];
        state.groups.hash = {};
        groups.forEach((group) => {
          state.groups.ids.push(group.id);
          state.groups.hash[group.id] = {
            ...group,
          };
        });
      })
      .addCase(searchUsersAndGroupsThunk.fulfilled, (state, action) => {
        state.loading = false;
        const { users = [], groups = [], query } = action.payload;
        state.search.users.ids = [];
        state.search.users.hash = {};
        state.search.groups.ids = [];
        state.search.groups.hash = {};

        state.search.query = query;

        // users
        users.forEach((user) => {
          state.search.users.ids.push(user.id);
          state.search.users.hash[user.id] = {
            ...user,
            fullname: `${user.first_name} ${user.last_name}`,
          };
        });
        // groups
        groups.forEach((group) => {
          state.search.groups.ids.push(group.id);
          state.search.groups.hash[group.id] = {
            ...group,
          };
        });
      })
      .addCase(ownersListThunk.fulfilled, (state, action) => {
        const {
          meta: {
            arg: { includeInSearch },
          },
          payload: { users = [], groups = [] },
        } = action;

        state.loading = false;
        state.users.ids = [];
        state.users.hash = {};
        state.groups.ids = [];
        state.groups.hash = {};

        // users
        users.forEach((user) => {
          state.users.ids.push(user.id);
          state.users.hash[user.id] = {
            ...user,
            fullname: `${user.first_name} ${user.last_name}`,
          };
        });
        // groups
        groups.forEach((group) => {
          state.groups.ids.push(group.id);
          state.groups.hash[group.id] = {
            ...group,
          };
        });

        if (includeInSearch) {
          state.search.users.ids = [];
          state.search.users.hash = {};
          state.search.groups.ids = [];
          state.search.groups.hash = {};
          // users
          users.forEach((user) => {
            state.search.users.ids.push(user.id);
            state.search.users.hash[user.id] = {
              ...user,
              fullname: `${user.first_name} ${user.last_name}`,
            };
          });
          // groups
          groups.forEach((group) => {
            state.search.groups.ids.push(group.id);
            state.search.groups.hash[group.id] = {
              ...group,
            };
          });
        }
      })
      .addCase(getCreditsThunk.fulfilled, (state, action) => {
        state.loading = false;

        state.credits = {
          ...state.credits,
          ...prepareCredits(action.payload),
        };
      })
      .addCase(updateTLevelThunk.fulfilled, (state, action) => {
        const user = action.payload;
        state.loading = false;
        if (state.users.hash[user.id]) {
          state.users.hash[user.id] = user;
        }
      })
      .addCase(fetchUserThunk.fulfilled, (state, action) => {
        const user = action.payload;
        if (!state.users.ids.includes(user.id)) {
          state.users.ids.push(user.id);
        }
        state.users.hash[user.id] = userDTO(user);
      })
      .addCase(updateLocalStorageThunk.fulfilled, (state, action) => {
        const data = action.payload;
        state.loading = false;
        state.localStorage = data.data.local_storage || {};
        const lastUsedVoices =
          (Boolean(data?.data?.local_storage) &&
            data?.data?.local_storage[LOCALSTORAGE_ITEMS.lastUsedVoices]) ||
          null;
        if (lastUsedVoices) {
          localStorageService.setItem(
            LOCALSTORAGE_ITEMS.lastUsedVoices,
            lastUsedVoices
          );
        }
      })
      .addCase(updateUserDomainInfoThunk.fulfilled, (state, action) => {
        const user = action.payload;
        proceedUser(state, user);
      })
      .addCase(updateDemoWizardThunk.fulfilled, (state, action) => {
        const { data } = action.payload;
        state.loading = false;
        state.demoWizard = data.demo_wizard || {};
        state.demoWizard.ignoreWizard =
          !data?.demo_wizard ||
          !data?.demo_wizard.show ||
          data?.demo_wizard?.completed ||
          data?.demo_wizard?.skipped;
      })
      .addCase(updateSettingsThunk.fulfilled, (state, action) => {
        const user = action.payload;
        state.loadingSettings = false;
        proceedUser(state, user);
      })
      .addCase(sendPromoCodeThunk.fulfilled, (state, action) => {
        const user = action.payload;
        proceedUser(state, user);
        state.isPromoCodeResultReceived = true;
      })
      .addCase(getApiKeyThunk.fulfilled, (state, action) => {
        const data = action.payload;
        state.loading = false;
        state.apiKey = data;
      })
      .addCase(createApiKeyThunk.fulfilled, (state, action) => {
        const data = action.payload;
        state.loading = false;
        state.apiKey = data;
      })
      .addCase(deleteApiKeyThunk.fulfilled, (state) => {
        state.loading = false;
        state.apiKey = initialState.apiKey;
      })
      .addCase(npsSurveyThunk.fulfilled, (state) => {
        state.loading = false;
      })
      // #endregion
      // #region rejected
      .addCase(loginThunk.rejected, (state, action) => {
        state.loading = false;
        const { payload } = action;
        state.error = payload;
      })
      .addCase(signupThunk.rejected, (state, action) => {
        state.loading = false;
        const { payload } = action;
        state.error = payload;
      })
      .addCase(oauth2LoginThunk.rejected, (state, action) => {
        state.loading = false;
        const { payload } = action;
        state.error = payload;
      })
      .addCase(verifyEmailThunk.rejected, (state) => {
        state.loading = false;
        state.emailVerification = EMAIL_VERIFICATION_STATUS.FAIL;
      })
      .addCase(resendVerifyEmailThunk.rejected, (state) => {
        state.loading = false;
      })
      .addCase(changePasswordThunk.rejected, (state) => {
        state.loading = false;
        state.passwordUpdate = PASSWORD_UPDATE_STATUS.FAIL;
      })
      .addCase(updateSettingsThunk.rejected, (state) => {
        state.loadingSettings = false;
      })
      // #endregion
      .addCase(clearStore, () => initialState)
      .addCase(loginThunk.fulfilled, (state, action) => {
        state.loading = false;
        const { user } = action.payload;
        // actually don't need this because we're redirecting user after login (in actions)
        // proceedUser(state, user);
        const lastUsedVoices =
          (Boolean(user?.data?.local_storage) &&
            user?.data?.local_storage[LOCALSTORAGE_ITEMS.lastUsedVoices]) ||
          null;
        if (lastUsedVoices) {
          localStorageService.setItem(
            LOCALSTORAGE_ITEMS.lastUsedVoices,
            lastUsedVoices
          );
        }
      })
      .addMatcher(isPendingAction(actionsToFilter.pending), (state) => {
        state.loading = true;
      })
      .addMatcher(
        isFulfilledAction(actionsToFilter.fulfilled),
        (state, action) => {
          state.loading = false;
          const { user } = action.payload;
          proceedUser(state, user);
          /*
          logger.info("action", action);
          const user = action.payload;
          const { roles } = user;
          state.loadingGlobal = false;
          state.loading = false;

          state.user = userDTO(user);
          state.credits.creditLimit = user.data.credit_limit || false;

          state.isAuthenticated = true;
          state.isSubscribed = isUserSubscribed(user);
          state.isTrial = false;
          const stripeData = user?.data?.stripe;
          if (roles.includes(USER_ROLES.ADMIN)) {
            state.isAdmin = true;
          } else if (roles.includes(USER_ROLES.PREPAID)) {
            state.isPrepaid = true;
          }
          if (stripeData && stripeData.subscription_status) {
            state.isTrial =
              stripeData.trial &&
              stripeData.subscription_status === SUBSCRIPTION_STATUS.TRIALING;
          }
          state.longConversionsAllowed = user.data.long_conversions;
          state.verified = user.verified;
          // FIXME
          // if (action.type === STRIPE_START_TRIAL_SUCCESS) {
          //   draft.trialStarted = true;
          // }
          */
        }
      )
      .addMatcher(
        isRejectedAction(actionsToFilter.rejected),
        (state, action) => {
          logger.info("action", action);
          const { error } = action;
          state.loadingGlobal = false;
          state.loading = false;
          state.error = error;
        }
      );
  },
});

export const actions = authSlice.actions;

export default authSlice.reducer;
